import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import Tag from '@frontend/app/refresh-components/Tag';

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@frontend/shadcn/components/ui/sheet';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Input } from '@frontend/shadcn/components/ui/input';
import { Checkbox } from '@frontend/shadcn/components/ui/checkbox';
import DateFilter from '@frontend/applications/ReportsApp/pages/Reporting/components/DateFilter/DateFilter';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@frontend/shadcn/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@frontend/shadcn/components/ui/popover';
import { isEmpty, startCase } from 'lodash';
import { SearchOutlined } from '@ant-design/icons';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';

import { TableColumnsIcon, ArrowDownToLineIcon, XmarkIcon, ChevronUpIcon, ChevronDownIcon, ArrowDownArrowUpIcon, CopyIcon } from '@revfluence/fresh-icons/regular/esm';
import { DataTable, TablePagination } from '@frontend/app/refresh-components/DataTableV2';
import { logger } from '@common';
import { message } from '@revfluence/fresh';
import { DateRangesLabel } from '@frontend/app/containers/HomePageMetrics/components/DateSelect';
import { dateValues } from '@frontend/applications/ReportsApp/pages/Reporting/components/Header/Header';
import { useGetOfferAffiliateConversionHistoryById } from '../../hooks/useGetOfferAffiliateConversionHistoryById';
import { getLocaleString, getTimeStamp } from '../../utils/dateTimeUtils';
import { OFFER_SOURCE } from '../../types/globalTypes';
import { GetOfferAffiliateConversionHistoryById_offerAffiliateConversionHistory } from '../../queries/types/GetOfferAffiliateConversionHistoryById';
import { CONVERSION_SOURCE, CUSTOMER_TYPE } from './types';
import { CONVERSION_STATUS } from '../../utils/getTagType';
import { DataFormat, formatValue } from '../../utils';
import { getTagColor } from '../../constants/offers';

interface IProps {
  offerId: number,
  onClose: () => void,
  open: boolean,
  source: OFFER_SOURCE,
}
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
const SortButton = ({ column, children }: { column: any; children: React.ReactNode }) => {
  const isSorted = column.getIsSorted();
  const [isHovering, setIsHovering] = React.useState(false);

  const handleSort = () => {
    if (isSorted === 'asc') {
      column.toggleSorting(true);
    } else if (isSorted === 'desc') {
      column.clearSorting();
    } else {
      column.toggleSorting(false);
    }
  };

  return (
    <Button
      variant="ghost"
      className="px-2 hover:bg-transparent w-full"
      onClick={handleSort}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="flex w-full justify-between items-center">
        <span>{children}</span>
        <span className="h-3 w-3 flex items-center justify-center">
          {isSorted === 'asc' && <ChevronUpIcon className="h-3 w-3" />}
          {isSorted === 'desc' && <ChevronDownIcon className="h-3 w-3" />}
          {!isSorted && isHovering && <ArrowDownArrowUpIcon className="h-3 w-3" />}
        </span>
      </div>
    </Button>
  );
};

function exportToCSV(columns: { headerName: string; field: string }[], data, filename = 'export.csv') {
  const headers = columns.map((col) => col.headerName).join(',');
  const rows = data.map((row) =>
    columns.map((col) => {
      const value = row[col.field];
      if (typeof value === 'string') {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value ?? '';
    }).join(',')
  );
  const csvContent = [headers, ...rows].join('\r\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export const RefreshOfferConversionHistoryDrawer: React.FC<IProps> = ({
  offerId,
  onClose,
  open,
  source,
}) => {
  const [sorting, setSorting] = useState<{ id: string; desc: boolean }[]>([]);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const [offerConversionHistory, setOfferConversionHistory] = useState<GetOfferAffiliateConversionHistoryById_offerAffiliateConversionHistory[]>([]);
  const { currencyConversionForLink } = useClientFeatures();
  const [dateRange, setDateRange] = useState<[Date?, Date?]>([null, null]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const offerConversionHistoryQuery = useGetOfferAffiliateConversionHistoryById({
    variables: {
      offerId: Number(offerId),
    },
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (!offerConversionHistoryQuery.loading && offerConversionHistoryQuery.data) {
      const data = offerConversionHistoryQuery.data.offerAffiliateConversionHistory || [];
      // Sort by conversion date in descending order (newest first)
      const sortedData = [...data].sort((a, b) =>
        new Date(b.conversionDate).getTime() - new Date(a.conversionDate).getTime()
      );
      setOfferConversionHistory(sortedData);
    }
  }, [offerConversionHistoryQuery.loading, offerConversionHistoryQuery.data]);

  // Reset page when date range changes
  useEffect(() => {
    setPage(1);
  }, [dateRange]);

  const getCustomerType = (isNewCustomer: boolean | null): string => {
    if (isNewCustomer === null) return '';
    return isNewCustomer ? CUSTOMER_TYPE.NEW_CUSTOMER : CUSTOMER_TYPE.RETURNING_CUSTOMER;
  };

  const data = useMemo(() => {
    return offerConversionHistory.map((conversion) => {
      let dupDiscountCode: string | undefined;
      try {
        if (conversion.discountCode) {
          const duplicateDiscount = JSON.parse(conversion.discountCode);
          if (Array.isArray(duplicateDiscount) && duplicateDiscount.length > 0 && duplicateDiscount[0]?.code) {
            dupDiscountCode = duplicateDiscount[0].code;
          }
        }
      } catch (error) {
        logger.error('Error parsing discount code', error);
      }

      return {
        conversionDate: conversion.conversionDate,
        id: String(conversion.conversionId),
        conversionId: conversion.conversionId,
        key: String(conversion.conversionId),
        sales: {
          sale: conversion.sale,
          currency: conversion.currency,
        },
        orderId: {
          id: conversion.conversionId,
          url: conversion.orderStatusUrl,
        },
        exchangeRates: {
          rate: conversion.exchangeRate,
          currency: conversion.currency,
        },
        saleAmount: {
          sale: conversion.sale,
          baseSale: conversion.salesBase,
          currency: conversion.currency,
        },
        commissionEarned: conversion.payoutEarned,
        status: conversion.status,
        affiliatInfoOne: conversion.affiliatInfoOne,
        tuneOrderId: conversion.tuneOrderId,
        memberName: conversion.memberName,
        promoCode: conversion.promoCode || '--',
        customerType: getCustomerType(conversion.isNewCustomer),
        duplicateDiscountCode: dupDiscountCode,
        source: conversion.source === 'link' ? CONVERSION_SOURCE.LINK : CONVERSION_SOURCE.PROMO,
      };
    });
  }, [offerConversionHistory]);

  const isUsdStore = useMemo(() => {
    return data.every((item) => item.saleAmount.currency === 'USD');
  }, [data]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        id: 'conversionDate',
        headerName: 'Date',
        accessorKey: 'conversionDate',
        cell: ({ row }) => getLocaleString(row.original.conversionDate),
        header: ({ column }) => (
          <SortButton column={column}>Date</SortButton>
        ),
        size: 200,
        minSize: 200,
        maxSize: 200,
      },
      {
        id: 'orderId',
        headerName: 'Order ID',
        accessorKey: 'orderId',
        cell: ({ row }) => {
          const record = row.original;
          return record.source === CONVERSION_SOURCE.LINK ? (
            <span>{record.tuneOrderId}</span>
          ) : (
            <a href={record.orderId.url} target="_blank" rel="noopener noreferrer">
              {record.orderId.id}
            </a>
          );
        },
        header: ({ column }) => (
          <SortButton column={column}>Order ID</SortButton>
        ),
      },
      {
        id: 'saleAmount',
        headerName: 'Sales',
        accessorKey: 'saleAmount',
        cell: ({ row }) => {
          const record = row.original;
          return (
            <div className="text-right w-full">
              {isUsdStore || (record.source === CONVERSION_SOURCE.LINK && !currencyConversionForLink)
                ? `$${formatValue(DataFormat.Money, record.saleAmount.sale || '-')}`
                : `${formatValue(DataFormat.Money, record.saleAmount.baseSale || '-')} ${!isUsdStore ? record.saleAmount.currency : ''}`}
            </div>
          );
        },
        header: ({ column }) => (
          <SortButton column={column}>Sales</SortButton>
        ),
        size: 150,
        minSize: 150,
        maxSize: 150,
      },
      {
        id: 'commissionEarned',
        headerName: 'Commission Earned',
        accessorKey: 'commissionEarned',
        cell: ({ row }) => (
          <div className="text-right w-full">
            $ {formatValue(DataFormat.Money, row.original.commissionEarned)}
          </div>
        ),
        header: ({ column }) => (
          <SortButton column={column}>Commission Earned</SortButton>
        ),
        size: 200,
        minSize: 200,
        maxSize: 200,
      },
      {
        id: 'status',
        headerName: 'Status',
        accessorKey: 'status',
        cell: ({ row }) => {
          const record = row.original;
          if (record.duplicateDiscountCode) {
            return (
              <Tag label={startCase(CONVERSION_STATUS.DUPLICATE)} bgColor={getTagColor(CONVERSION_STATUS.DUPLICATE)} textColor="text-primary" />

            );
          }
          return <Tag label={startCase(record.status)} bgColor={getTagColor(record.status)} textColor="text-primary" />;
        },
        header: ({ column }) => (
          <SortButton column={column}>Status</SortButton>
        ),
        size: 150,
        minSize: 150,
        maxSize: 150,
      },
      {
        id: 'memberName',
        headerName: 'Affiliate',
        accessorKey: 'memberName',
        cell: ({ row }) => {
          const memberName = row.original.memberName;
          const displayText = memberName && memberName.length > 20
            ? `${memberName.substring(0, 20)}...`
            : memberName;

          return memberName && memberName.length > 20 ? (
            <Tooltip>
              <TooltipTrigger>
                <span>{displayText}</span>
              </TooltipTrigger>
              <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 w-max">
                {memberName}
              </TooltipContent>
            </Tooltip>
          ) : (
            <span>{memberName}</span>
          );
        },
        header: ({ column }) => (
          <SortButton column={column}>Affiliate</SortButton>
        ),
        size: 200,
        minSize: 200,
        maxSize: 200,
      },
    ];

    if ((source === OFFER_SOURCE.TUNE ? currencyConversionForLink : true) && !isUsdStore) {
      baseColumns.push(
        {
          id: 'exchangeRates',
          headerName: 'Exchange Rate(USD)',
          accessorKey: 'exchangeRates',
          cell: ({ row }) => {
            const record = row.original;
            return (
              <div className="text-right w-full">
                {(record.source === CONVERSION_SOURCE.PROMO || currencyConversionForLink) ? (
                  <Tag bgColor="bg-grey-2" textColor="text-primary" label={`1 ${record.exchangeRates.currency} = ${Number(record.exchangeRates.rate).toFixed(4)} USD`} />
                ) : '--'}
              </div>
            );
          },
          header: ({ column }) => (
            <SortButton column={column}>Exchange Rate(USD)</SortButton>
          ),
          size: 200,
          minSize: 200,
          maxSize: 200,
        },
        {
          id: 'sales',
          headerName: 'Sales (USD)',
          accessorKey: 'sales',
          cell: ({ row }) => {
            const record = row.original;
            return (
              <div className="text-right w-full">
                {(record.source === CONVERSION_SOURCE.PROMO || currencyConversionForLink)
                  ? `$ ${formatValue(DataFormat.Money, record.sales.sale)}`
                  : '--'}
              </div>
            );
          },
          header: ({ column }) => (
            <SortButton column={column}>Sales (USD)</SortButton>
          ),
          size: 200,
          minSize: 200,
          maxSize: 200,
        }
      );
    }

    if (source === OFFER_SOURCE.SHOPIFY || source === OFFER_SOURCE.PROMO_LINK) {
      baseColumns.push(
        {
          id: 'promoCode',
          headerName: 'Promo Codes',
          accessorKey: 'promoCode',
          cell: ({ row }) => (
            <Tooltip>
              <TooltipTrigger>
                <div className="flex items-center w-48 min-h-[24px] gap-2">
                  <span className="truncate w-[120px]">
                    {row.original.promoCode.length > 15
                      ? `${row.original.promoCode.substring(0, 15)}...`
                      : row.original.promoCode}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 hover:bg-grey-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(row.original.promoCode)
                        .then(() => {
                          message.success('Promo code copied to clipboard');
                        })
                        .catch(() => {
                          message.error('Failed to copy promo code');
                        });
                    }}
                    aria-label="Copy promo code"
                  >
                    <CopyIcon className="w-4 h-4 text-primary" />
                  </Button>
                </div>
              </TooltipTrigger>
              <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 w-max">
                {row.original.promoCode}
              </TooltipContent>
            </Tooltip>
          ),
          header: ({ column }) => (
            <SortButton column={column}>Promo Codes</SortButton>
          ),
          size: 180,
          minSize: 180,
          maxSize: 180,
        },
        {
          id: 'customerType',
          headerName: 'Customer',
          accessorKey: 'customerType',
          cell: ({ row }) => {
            const text = row.original.customerType;
            return text ? (
              <Badge variant="outline" className={text === CUSTOMER_TYPE.NEW_CUSTOMER ? 'bg-yellow-100' : 'bg-blue-100'}>
                {text}
              </Badge>
            ) : <span>-</span>;
          },
          header: ({ column }) => (
            <SortButton column={column}>Customer</SortButton>
          ),
          size: 200,
          minSize: 200,
          maxSize: 200,
        }
      );
    }

    if (source === OFFER_SOURCE.PROMO_LINK) {
      baseColumns.push({
        id: 'conversion_source',
        headerName: 'Source',
        accessorKey: 'source',
        cell: ({ row }) => (
          <Badge variant="outline" className={row.original.source === CONVERSION_SOURCE.LINK ? 'bg-blue-100' : 'bg-purple-100'}>
            {row.original.source}
          </Badge>
        ),
        header: ({ column }) => (
          <SortButton column={column}>Source</SortButton>
        ),
        size: 150,
        minSize: 150,
        maxSize: 150,
      });
    }

    return baseColumns;
  }, [source, isUsdStore, currencyConversionForLink]);

  const filteredData = useMemo(() => {
    const [startDate, endDate] = dateRange;

    // First apply date filtering
    const dateFilteredData = data.filter((item) => {
      if (!startDate && !endDate) return true;

      const conversionTimestamp = getTimeStamp(item.conversionDate);
      const startTimestamp = startDate ? getTimeStamp(new Date(startDate).setHours(0, 0, 0, 0)) : null;
      const endTimestamp = endDate ? getTimeStamp(new Date(endDate).setHours(23, 59, 59, 999)) : null;

      if (startTimestamp && endTimestamp) {
        return conversionTimestamp >= startTimestamp && conversionTimestamp <= endTimestamp;
      } else if (startTimestamp) {
        return conversionTimestamp >= startTimestamp;
      } else if (endTimestamp) {
        return conversionTimestamp <= endTimestamp;
      }

      return true;
    });

    // Then apply search filtering
    if (!searchText) return dateFilteredData;

    const searchLower = searchText.toLowerCase();
    return dateFilteredData.filter((item) => {
      return (
        item.orderId.id.toString().toLowerCase().includes(searchLower) ||
        item.memberName?.toLowerCase().includes(searchLower) ||
        item.promoCode?.toLowerCase().includes(searchLower) ||
        item.status?.toLowerCase().includes(searchLower)
      );
    });
  }, [data, searchText, dateRange]);

  const sortedData = useMemo(() => {
    if (!sorting.length) return filteredData;
    const [{ id, desc }] = sorting;
    return [...filteredData].sort((a, b) => {
      let aValue; let
        bValue;

      // Handle nested object properties
      switch (id) {
        case 'orderId':
          aValue = a.orderId.id;
          bValue = b.orderId.id;
          break;
        case 'saleAmount':
          aValue = a.saleAmount.sale;
          bValue = b.saleAmount.sale;
          break;
        case 'sales':
          aValue = a.sales.sale;
          bValue = b.sales.sale;
          break;
        case 'exchangeRates':
          aValue = a.exchangeRates.rate;
          bValue = b.exchangeRates.rate;
          break;
        default:
          aValue = a[id as keyof typeof a];
          bValue = b[id as keyof typeof b];
      }

      if (aValue === bValue) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return desc ? bValue - aValue : aValue - bValue;
      }
      return desc
        ? String(bValue).localeCompare(String(aValue))
        : String(aValue).localeCompare(String(bValue));
    });
  }, [filteredData, sorting]);

  const totalPages = Math.ceil(filteredData.length / pageSize);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return sortedData.slice(start, end);
  }, [sortedData, page, pageSize]);

  const csvColumnConfig = useMemo(() => [
    { headerName: 'Date', field: 'conversionDate' },
    { headerName: 'Order ID', field: 'orderId' },
    { headerName: 'Sales', field: 'sales' },
    { headerName: 'Sales Currency', field: 'salesCurrency' },
    { headerName: 'Commission Earned', field: 'commissionEarned' },
    { headerName: 'Status', field: 'status' },
    { headerName: 'Affiliate', field: 'memberName' },
    { headerName: 'Exchange Rate', field: 'exchangeRate' },
    { headerName: 'Promo Code', field: 'promoCode' },
    { headerName: 'Customer Type', field: 'customerType' },
  ], []);
  const csvData = filteredData.map((row) => ({
    conversionDate: row.conversionDate ? format(new Date(row.conversionDate), 'MMM d, yyyy h:mm a') : '',
    orderId: source === OFFER_SOURCE.TUNE ? row.tuneOrderId : row.orderId.id,
    sales: row.saleAmount?.sale ? row.saleAmount.sale.toFixed(2) : '',
    salesCurrency: row.saleAmount?.currency ?? '',
    commissionEarned: row.commissionEarned ? row.commissionEarned.toFixed(2) : '',
    status: row.status,
    memberName: row.memberName,
    exchangeRate: row.exchangeRates
      ? `1 ${row.exchangeRates.currency} = ${row.exchangeRates.rate ? row.exchangeRates.rate.toFixed(2) : '0'} USD`
      : '',
    promoCode: row.promoCode,
    customerType: row.customerType,
  }));

  const onClickExport = useCallback(() => {
    exportToCSV(csvColumnConfig, csvData, 'conversions.csv');
  }, [csvColumnConfig, csvData]);

  const visibleColumns = useMemo(
    () => columns.filter((col) => columnVisibility[col.id] !== false),
    [columns, columnVisibility]
  );

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="flex flex-col h-full bg-grey-0 p-0 gap-0 w-[1129px] !max-w-[1129px] overflow-x-hidden">
        <SheetHeader className="flex items-center gap-2 flex-row p-4">
          <SheetTitle className="!m-0 text-base font-medium">Offer Conversions Report</SheetTitle>
        </SheetHeader>
        <div className="border-b" />
        <Card className="flex items-start gap-4 rounded-2xl m-4">
          <CardContent className="p-0 w-full flex flex-col">
            <div className="flex items-center justify-between px-4 py-2 border-b">
              {/* Count */}
              <div className="text-sm font-normal text-primary">{filteredData.length} Conversions</div>
              <div className="flex items-center gap-x-2">
                {/* Search */}
                <div>
                  <DateFilter
                    key="date-filter"
                    defaultLabel={DateRangesLabel.ALL_TIME}
                    isCheckbox={false}
                    options={dateValues}
                    isCustomDate={false}
                    onFilterChange={([filter]) => {
                      if (filter) {
                        setDateRange(filter.value.dateRange);
                        setPage(1); // Reset to first page when date filter changes
                      }
                    }}
                    data-testid="date-filter"
                    textColor="text-grey-6"
                  />
                </div>
                <div className="relative flex items-center">
                  <Button
                    variant="ghost"
                    className="w-8 h-8 flex items-center justify-center z-20"
                    size="icon"
                    onClick={() => setIsSearchOpen((v) => !v)}
                    aria-label="Search"
                  >
                    {isSearchOpen ? (
                      <XmarkIcon className="text-muted-foreground" />
                    ) : (
                      <SearchOutlined className="text-muted-foreground" />
                    )}
                  </Button>
                  <div
                    className={`absolute right-10 top-0 transition-all duration-300 ${isSearchOpen ? 'opacity-100 w-80' : 'opacity-0 w-0'} overflow-hidden`}
                    style={{ zIndex: 10 }}
                  >
                    <Input
                      value={searchText}
                      onChange={(e) => {
                        setSearchText(e.target.value);
                        setPage(1); // Reset to first page when search changes
                      }}
                      type="search"
                      placeholder={`Search ${source === OFFER_SOURCE.SHOPIFY ? 'Order ID, Affiliate, Promo Code' : 'Order ID, Affiliate, Event ID'}`}
                      className="rounded-lg bg-background pl-8 w-80 focus:ring-0 focus-visible:ring-0 outline-none"
                      autoFocus={isSearchOpen}
                    />
                    <SearchOutlined className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
                {/* Export */}
                <Tooltip>
                  <TooltipTrigger>
                    <Button
                      disabled={isEmpty(filteredData)}
                      onClick={onClickExport}
                      variant="ghost"
                      size="icon"
                      aria-label="Export conversions"
                    >
                      <ArrowDownToLineIcon />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]">
                    Export conversions
                  </TooltipContent>
                </Tooltip>
                {/* Column Visibility */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={isEmpty(filteredData)}
                      aria-label="Toggle column visibility"
                    >
                      <TableColumnsIcon />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-56 p-4 pointer-events-auto select-text z-[9999]" onClick={(e) => e.stopPropagation()}>
                    <div className="flex flex-col gap-2">
                      <h4 className="font-medium mb-2">Visible Columns</h4>
                      {columns.map((column) => (
                        <label key={column.id} className="flex items-center gap-2">
                          <Checkbox
                            checked={columnVisibility[column.id] !== false}
                            onCheckedChange={(checked) => {
                              setColumnVisibility((prev) => ({
                                ...prev,
                                [column.id]: checked === true,
                              }));
                            }}
                          />
                          <span>{column.headerName}</span>
                        </label>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                {/* Pagination */}
                <div className="flex gap-2 items-center ml-4">
                  <TablePagination
                    pageIndex={page - 1}
                    pageSize={pageSize}
                    totalCount={filteredData.length}
                    canPreviousPage={page > 1}
                    canNextPage={page < totalPages}
                    onPaginationChange={(newPage) => setPage(newPage + 1)}
                  />
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-x-hidden w-full">
              <DataTable
                columns={visibleColumns}
                data={paginatedData}
                enableColumnResizing
                getRowId={(row) => row.id.toString()}
                columnVisibility={columnVisibility}
                onColumnVisibilityChange={setColumnVisibility}
                onSortingChange={setSorting}
                isLoading={offerConversionHistoryQuery.loading}
                pinnedLeft={['conversionDate']}
              />
            </div>
          </CardContent>
        </Card>
      </SheetContent>
    </Sheet>
  );
};
